import { useState } from "react";
import { useNavigate } from "react-router-dom";

import toast from "components/partials/toast/toast";

import { useCurrentClient } from "hooks/api/use-clients";
import { getProgramTemplates } from "generated/api/program-templates";
import type { CloneProgramTemplateItemRequest } from "generated/schemas";
import type { DisplayedProgramTemplate } from "containers/admin/clients/client/program-templates-page/components/program-templates-table/program-templates-table.helper";
import { LoadingButtonState } from "ui/button";
import type { CloneProgramTemplatesFormData } from "components/modals/clone-program-templates-modal/clone-program-templates-modal.types";

import { getTouchpointPaths } from "utilities/app-routes";

interface UseCloneProgramTemplatesProps {
  selectedTemplates: DisplayedProgramTemplate[];
  onClose: () => void;
  onSuccess?: () => void;
}

export const useCloneProgramTemplates = ({
  selectedTemplates,
  onClose,
  onSuccess,
}: UseCloneProgramTemplatesProps) => {
  const currentClient = useCurrentClient();
  const navigate = useNavigate();
  const [submitState, setSubmitState] = useState<LoadingButtonState>(LoadingButtonState.Default);

  const handleSubmit = async (data: CloneProgramTemplatesFormData) => {
    setSubmitState(LoadingButtonState.Loading);

    const items: CloneProgramTemplateItemRequest[] = [];
    for (const [i, template] of selectedTemplates.entries()) {
      const templateTouchpointVersionId = template.latestVersion?.id;
      if (!templateTouchpointVersionId) {
        toast.error({
          data: {
            title: "Failed to clone templates",
            message: `"${template.name}" has no version to clone.`,
          },
        });
        setSubmitState(LoadingButtonState.Default);
        return;
      }
      items.push({
        templateTouchpointVersionId,
        name: data.items[i].name,
        applyClientTheme: data.items[i].applyClientTheme,
      });
    }

    try {
      const programTemplatesApi = getProgramTemplates();
      const clonedTemplates = await programTemplatesApi.cloneProgramTemplates(
        { clientId: currentClient.id },
        { items },
      );

      setSubmitState(LoadingButtonState.Success);

      const count = items.length;
      toast.success({
        data: {
          title: `Cloned ${count} template${count > 1 ? "s" : ""}`,
        },
      });

      onSuccess?.();

      setSubmitState(LoadingButtonState.Default);
      onClose();

      if (clonedTemplates.length === 1) {
        const cloned = clonedTemplates[0];
        const { touchpointPreviewPath } = getTouchpointPaths({
          clientId: cloned.clientId,
          campaignId: cloned.campaignId,
          touchpointId: cloned.id,
        });
        navigate(touchpointPreviewPath);
      }
    } catch (error) {
      console.error(error);
      toast.error({
        data: {
          title: "Failed to clone templates",
        },
      });
      setSubmitState(LoadingButtonState.Default);
    }
  };

  const handleCancelClick = () => {
    setSubmitState(LoadingButtonState.Default);
    onClose();
  };

  return {
    submitState,
    handleSubmit,
    handleCancelClick,
  };
};
